import { ArpeggiCompositionV5, serializeStemSnapshot, StemType } from '@arpeggi/shared'
import { toJS } from 'mobx'
import {
  cast,
  getRoot,
  getSnapshot,
  getType,
  SnapshotIn,
  SnapshotOut,
  types
} from 'mobx-state-tree'
import { nanoid } from 'nanoid'
import { ForDirectExtend } from '../../../features/mobx'
import { ILoopRefStore, LoopRefStore } from './loop/LoopRefStore'
import { IAudioStemStore } from './stem/AudioStemStore'
import { IDrumStemStore } from './stem/DrumStemStore'
import { IInstrumentStemStore } from './stem/InstrumentStemStore'
import { StemSettingsStore } from './StemSettingsStore'

/**
 * Base model for all stems in a song. Extended by InstrumentStemStore, DrumStemStore, and AudioStemStore.
 */
const _StemStore = types
  .model({
    // A unique identifier assigned to the stem at creation time and unchanged thereafter.
    // We use this especially in the construction of the audio graph to keep track of stems
    // as they're added/removed/reordered.
    uniqueId: types.maybe(types.string),
    // loops
    loops: types.array(LoopRefStore),
    // Stem settings can be overridden when remixing.
    stemSettings: types.maybe(StemSettingsStore),
    // If this is a remixed stem, immutable and show these values.
    isRemixed: false,
    // Written to contract
    remixedContractId: -1,
    // For genesis, this is uniquely saved as 4 * genesisSongIndex + stemIndex (0-3)
    remixedStemId: -1,
    // Only for display
    remixedOgSongName: types.maybe(types.string),
    // Only for display
    remixedOgArtist: types.maybe(types.string),
    // TODO: make it clear that this is usable
    colorIndex: -1,
    // Is this track currently set to be soloed?
    isSolo: false,
    // The color to represent the token as
    tokenColor: types.maybeNull(types.string)
  })
  .views((self) => ({
    type(): string {
      return getType(self).name
    }
  }))
  .views((self) => ({
    // Returns null if no loop at this section
    getLoopAtSectionOrNull(section: number): ILoopRefStore {
      if (section >= self.stemSettings.arrangement.length || section < 0) return null
      var loopIndex = self.stemSettings.arrangement[section]
      if (loopIndex === 0) return null
      return self.loops[loopIndex - 1]
    },

    getStemGain(): number {
      return self.stemSettings.stemGain
    },
    getLoopIndex(section: number) {
      if (section >= self.stemSettings.arrangement.length) {
        return 0
      }
      return self.stemSettings.arrangement[section]
    },

    studio() {
      return toJS(getRoot(self) as any).studio
    },

    // Length of stem including empty arrangement at end.
    get stemLength(): number {
      if (self.type() === StemType.AUDIO) {
        return Math.max.apply(
          null,
          (self as IAudioStemStore).clips.map((clip) => clip.endSection + 16)
        )
      }
      return self.stemSettings.arrangement.length
    },
    // Length of stem only up to the last populated arrangement
    get stemRealLength(): number {
      if (getType(self).name === StemType.AUDIO) {
        if ((self as any).clips.length === 0) return 0
        // we have to add 1 to the clip end section because this is a count of sections, not index
        return Math.max.apply(
          null,
          (self as IAudioStemStore).clips.map((clip) => clip.endSection + 1)
        )
      }
      let length = 0
      self.stemSettings.arrangement.forEach((loopIndex, index) => {
        if (loopIndex > 0) {
          length = index + 1
        }
      })
      return length
    }
  }))
  .actions((self) => {
    // Tracks which were the latest batch of notes to be played.
    // Used for autorelease.
    // TODO could replace with a single stem gain node.
    let currentlyPlayingNodes = []

    function afterCreate() {
      generateID(false)
    }

    function generateID(generateNewClipIDs: boolean = true) {
      setUniqueId(nanoid())
      if (self.type() === StemType.AUDIO && generateNewClipIDs) {
        const audioStem = self as any // specifically any to not cause a circular dependency
        audioStem.clips.forEach((clip) => clip.generateClipID())
      }
    }

    function setUniqueId(uniqueId: string) {
      self.uniqueId = uniqueId
    }

    function updateColorIndex(index: number) {
      self.colorIndex = index
    }

    /** Sets the arrangement to be only empty loops */
    function clearArrangement() {
      if (self.type() === StemType.AUDIO) {
        const audioStem = self as any // specifically any to not cause a circular dependency
        audioStem.clips = []
      } else {
        const emptyArrangement = self.stemSettings.arrangement.map((val) => 0)
        self.stemSettings.setEntireArrangement(emptyArrangement)
      }
    }

    /** Sets the arrangement order to [A, B, C... 0, 0] */
    function setArrangementABC() {
      const arrangementABC = []
      self.loops.forEach((_, index) => {
        arrangementABC[index] = index + 1
      })

      self.stemSettings.arrangement = cast(arrangementABC)
    }

    /**
     * Removes the loop at the given index (0 = A, 1 = B)
     * by "squishing" the loops array down. This means loops can't contain null.
     *
     * E.g., if loop 1 (B) is removed, sets all loops currently set to "B" to empty,
     * and renames loop C -> loop B, D -> C, etc.
     */
    function removeLoop(loopIndex: number) {
      if (loopIndex >= self.loops.length) {
        console.log('LoopIndex ', loopIndex, ' is larger than loops size, cant remove')
        return
      }
      // Moves the loopIndex of each arrangement down by 1.
      self.stemSettings.arrangement.forEach((currentLoopIndex, sectionIndex) => {
        // Sets all currentIndex loops to 0 to 0
        if (currentLoopIndex - 1 === loopIndex) {
          self.stemSettings.setArrangement(sectionIndex, 0)
        }
        // "Squishes" the remaining ones.
        if (currentLoopIndex - 1 > loopIndex) {
          self.stemSettings.setArrangement(sectionIndex, currentLoopIndex - 1)
        }
      })
      self.loops.splice(loopIndex, 1)
    }

    // Creates and adds a new default loopRef for this stem.
    // If drums, also autopopulates with the correct amount of subloops.
    function updateLoopInStem(section: number, value: number) {
      while (section + 16 >= self.stemSettings.arrangement.length) {
        self.stemSettings.extendArrangement()
      }
      self.stemSettings.arrangement[section] = value
    }

    // Shrinks the arrangement length to remove empty sections from the end.
    function trimArrangement() {
      self.stemSettings.trimArrangement()
    }
    function trimArrangementTo(length: number) {
      self.stemSettings.trimArrangementTo(length)
    }

    function updateStemSettings(stemSettings) {
      self.stemSettings = stemSettings
    }
    function setGain(gain: number) {
      self.stemSettings.stemGain = gain
    }

    function setCurrentlyPlayingNodes(newNodes) {
      currentlyPlayingNodes = newNodes
    }

    function getCurrentlyPlayingNodes() {
      return currentlyPlayingNodes
    }

    function updateRemixProperties(songArtist: string, songTitle: string) {
      self.isRemixed = true
      self.remixedOgSongName = songTitle
      self.remixedOgArtist = songArtist
    }
    function updateRemixedStemId(id: number) {
      self.remixedStemId = id
    }

    function solo() {
      self.isSolo = true
    }

    function unsolo() {
      self.isSolo = false
    }

    function serializeStemForStorage(): ArpeggiCompositionV5 {
      return serializeStemSnapshot(getSnapshot(self))
    }

    /** Removes unused or unnecessary parts of this stem for efficiency. */
    function minimizeForStorage() {
      removeUnusedLoops()
      removeBlankLoops()
    }

    function removeUnusedLoops() {
      const usedLoops = new Set<number>()

      self.stemSettings.arrangement.forEach((loopIndex) => {
        if (loopIndex === 0) return
        usedLoops.add(loopIndex - 1)
      })

      const unusedLoops = new Set<number>()

      self.loops.forEach((_, i) => {
        if (!usedLoops.has(i)) unusedLoops.add(i)
      })

      for (let i = self.loops.length - 1; i >= 0; i--) {
        if (unusedLoops.has(i)) removeLoop(i)
      }
    }

    function removeBlankLoops() {
      // TODO: write a function that removes loops that don't have any notes
      for (let i = self.loops.length - 1; i >= 0; i--) {
        const isEmpty = isLoopEmpty(i)
        if (isEmpty) removeLoop(i)
      }
    }

    function isLoopEmpty(index: number): boolean {
      const loop = self.loops[index]
      const type = self.type()
      if (type === StemType.DRUMS) {
        for (let i = 0; i < loop.drumLoop.subLoops.length; i++) {
          const subLoop = loop.drumLoop.subLoops[i]
          if (subLoop.notes.notes.length > 0) return false
        }
      } else if (type === StemType.INSTRUMENT) {
        if (loop.melodicLoop.notes.notes.length > 0) return false
      }

      return true
    }

    function setTokenColor(newColor: string) {
      self.tokenColor = newColor
    }

    /**
     * Transposes all of the loops in the stem up or down a specified number of steps
     * @param offset the number of steps to transpose the stem, can be positive or negative
     */
    function transposeBy(offset: number) {
      if (self.type() === StemType.INSTRUMENT)
        self.loops.forEach((loop) => loop.melodicLoop.transposeBy(offset))
    }

    return {
      afterCreate,
      updateLoopInStem,
      setGain,
      setCurrentlyPlayingNodes,
      getCurrentlyPlayingNodes,
      updateStemSettings,
      updateRemixProperties,
      updateRemixedStemId,
      removeLoop,
      updateColorIndex,
      solo,
      unsolo,
      clearArrangement,
      trimArrangement,
      trimArrangementTo,
      setArrangementABC,
      setUniqueId,
      minimizeForStorage,
      serializeStemForStorage,
      setTokenColor,
      transposeBy,
      generateID
    }
  })

interface StemStoreType extends ForDirectExtend<typeof _StemStore> {}
export const StemStore: StemStoreType = _StemStore

export type IStemStore = IDrumStemStore | IInstrumentStemStore | IAudioStemStore
export interface IStemStoreSnapshotIn extends SnapshotIn<typeof StemStore> {}
export interface IStemStoreSnapshotOut extends SnapshotOut<typeof StemStore> {}
